import { type OrderItemRecord, orderItemPricingFactsAreConsistent } from "@billiard/contracts";

export interface StandardOrderItemAddCommand {
  kind: "ADD_ORDER_ITEM";
  sessionId: string;
  tableId: string;
  tableNumber: string;
  skuId: string;
  productName: string;
  unitPriceFen: number;
  inventoryManaged: boolean;
  stockOnHandBefore: number | null;
  quantity: number;
  baselineItems: readonly OrderItemRecord[];
  idempotencyKey: string;
}

export interface GiftOrderItemAddCommand {
  kind: "GIFT_ORDER_ITEM";
  sessionId: string;
  tableId: string;
  tableNumber: string;
  skuId: string;
  productName: string;
  listUnitPriceFen: number;
  inventoryManaged: boolean;
  stockOnHandBefore: number | null;
  quantity: number;
  reason: string;
  baselineItems: readonly OrderItemRecord[];
  originalAuthorizerId: string;
  originalAuthorizerDisplayName: string;
  idempotencyKey: string;
}

export type OrderItemAddCommand = StandardOrderItemAddCommand | GiftOrderItemAddCommand;

export type OrderItemAddRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface OrderItemAddRecovery {
  ownerIdentityKey: string;
  command: OrderItemAddCommand;
  state: OrderItemAddRecoveryState;
}

export type OrderItemAddRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recoveries: readonly OrderItemAddRecovery[] }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE"; pendingOutcomeMayExist: boolean };

export type OrderItemAddRecoveryClaim =
  | {
      status: "OWNED";
      recovery: OrderItemAddRecovery;
      recoveries: readonly OrderItemAddRecovery[];
    }
  | { status: "OTHER_SESSION" }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE"; pendingOutcomeMayExist: boolean };

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

export const ORDER_ITEM_ADD_RECOVERY_STORAGE_KEY = "billiard-erp:order-item-add-recovery:v1";
const MAX_PENDING_ORDER_ITEM_ADDS = 50;

const definiteNoEffectCodes = new Set([
  "AUTH_REQUIRED",
  "AUTHORIZATION_DEFAULT_DENY",
  "CONDITIONAL_MODULE_DISABLED",
  "CSRF_REJECTED",
  "IDEMPOTENCY_COLLISION",
  "IDEMPOTENCY_KEY_REUSED",
  "GIFT_ORDER_ITEM_PRICE_INVALID",
  "GIFT_ORDER_ITEM_PRODUCT_CHANGED",
  "GIFT_ORDER_ITEM_REASON_INVALID",
  "INVENTORY_INSUFFICIENT",
  "INVALID_ORDER_ITEM_QUANTITY",
  "MEMBER_CHECKOUT_IN_PROGRESS",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_APP_VERSION_INVALID",
  "NATIVE_APP_VERSION_MISMATCH",
  "NATIVE_APP_VERSION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_SERVER_UPDATE_REQUIRED",
  "NATIVE_UPDATE_REQUIRED",
  "OFFLINE_AUTHORITY_UNAVAILABLE",
  "ORDER_CONFLICT_ISOLATED",
  "ORIGIN_REJECTED",
  "PRODUCT_DISABLED",
  "PRODUCT_SNAPSHOT_UNAVAILABLE",
  "PRODUCT_UNAVAILABLE",
  "REQUEST_VALIDATION_FAILED",
  "SECONDARY_AUTHORIZATION_DENIED",
  "SECONDARY_AUTHORIZATION_BINDING_MISMATCH",
  "SECONDARY_AUTHORIZATION_LOGIN_REQUIRED",
  "SECONDARY_AUTHORIZATION_REQUIRED",
  "SECONDARY_AUTHORIZATION_REQUIRES_MANAGER",
  "SESSION_NOT_OPEN",
]);

let pendingRecoveries: readonly OrderItemAddRecovery[] = Object.freeze([]);
const dispatchingRecoveries = new Set<OrderItemAddRecovery>();
let hydrated = false;
let recoveryUnavailable = false;
let pendingOutcomeMayExist = false;

export function orderItemAddIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function orderItemAddOutcomeIsUnknown(error: unknown): boolean {
  if (!isRecord(error)) return true;
  if (error.code === "GATEWAY_RESPONSE_INVALID" || error.code === "GATEWAY_UNREACHABLE") {
    return true;
  }
  if (typeof error.statusCode === "number") {
    if (error.statusCode === 408 || error.statusCode === 429) return true;
    if (error.statusCode >= 500) return true;
    if (error.statusCode >= 400) return false;
  }
  return !(typeof error.code === "string" && definiteNoEffectCodes.has(error.code));
}

export function orderItemAddRecoveryOutcomeIsUnknown(
  recovery: Pick<OrderItemAddRecovery, "state">,
  error: unknown,
): boolean {
  return recovery.state === "OUTCOME_UNKNOWN" || orderItemAddOutcomeIsUnknown(error);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedString(value: unknown, minimum: number, maximum: number): value is string {
  return typeof value === "string" && [...value].length >= minimum && [...value].length <= maximum;
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function isOrderItemRecord(value: unknown): value is OrderItemRecord {
  if (!isRecord(value)) return false;
  const requiredKeys = [
    "id",
    "skuId",
    "name",
    "unitPriceFen",
    "quantity",
    "returnedQuantity",
    "evidence",
  ] as const;
  const allowedKeys = new Set([
    ...requiredKeys,
    "productVersion",
    "lineType",
    "pricingSource",
    "listUnitPriceFen",
    "unitDiscountFen",
    "adjustmentReason",
    "authorizedByRole",
    "authorizedById",
    "sourceReservationId",
  ]);
  if (
    requiredKeys.some((key) => !Object.hasOwn(value, key)) ||
    Object.keys(value).some((key) => !allowedKeys.has(key)) ||
    !isBoundedString(value.id, 1, 256) ||
    !isBoundedString(value.skuId, 1, 128) ||
    !isBoundedString(value.name, 1, 120) ||
    !Number.isSafeInteger(value.unitPriceFen) ||
    (value.unitPriceFen as number) < 0 ||
    !Number.isSafeInteger(value.quantity) ||
    (value.quantity as number) < 1 ||
    !Number.isSafeInteger(value.returnedQuantity) ||
    (value.returnedQuantity as number) < 0 ||
    (value.returnedQuantity as number) > (value.quantity as number) ||
    (value.evidence !== "SIMULATED" && value.evidence !== "STORE_ACCEPTED")
  ) {
    return false;
  }
  if (
    Object.hasOwn(value, "productVersion") &&
    (!Number.isSafeInteger(value.productVersion) || (value.productVersion as number) < 1)
  ) {
    return false;
  }
  if (
    Object.hasOwn(value, "sourceReservationId") &&
    !isBoundedString(value.sourceReservationId, 1, 128)
  ) {
    return false;
  }
  return orderItemPricingFactsAreConsistent(value as unknown as OrderItemRecord);
}

function isOrderItemAddCommand(value: unknown): value is OrderItemAddCommand {
  if (!isRecord(value)) return false;
  const commonKeys = [
    "kind",
    "sessionId",
    "tableId",
    "tableNumber",
    "skuId",
    "productName",
    "inventoryManaged",
    "stockOnHandBefore",
    "quantity",
    "baselineItems",
    "idempotencyKey",
  ] as const;
  const kindSpecificKeys =
    value.kind === "ADD_ORDER_ITEM"
      ? ["unitPriceFen"]
      : value.kind === "GIFT_ORDER_ITEM"
        ? ["listUnitPriceFen", "reason", "originalAuthorizerId", "originalAuthorizerDisplayName"]
        : [];
  if (kindSpecificKeys.length === 0 || !hasExactKeys(value, [...commonKeys, ...kindSpecificKeys])) {
    return false;
  }
  if (
    !isBoundedString(value.sessionId, 1, 256) ||
    !isBoundedString(value.tableId, 1, 256) ||
    !isBoundedString(value.tableNumber, 1, 64) ||
    !isBoundedString(value.skuId, 1, 128) ||
    !isBoundedString(value.productName, 1, 120) ||
    value.sessionId !== value.sessionId.trim() ||
    value.tableId !== value.tableId.trim() ||
    value.tableNumber !== value.tableNumber.trim() ||
    value.skuId !== value.skuId.trim() ||
    typeof value.inventoryManaged !== "boolean" ||
    !Number.isSafeInteger(value.quantity) ||
    (value.quantity as number) < 1 ||
    (value.quantity as number) > 99 ||
    !Array.isArray(value.baselineItems) ||
    value.baselineItems.length > 10_000 ||
    !value.baselineItems.every(isOrderItemRecord) ||
    new Set(value.baselineItems.map((item) => item.id)).size !== value.baselineItems.length ||
    !isBoundedString(value.idempotencyKey, 8, 128) ||
    value.idempotencyKey !== value.idempotencyKey.trim()
  ) {
    return false;
  }
  if (value.kind === "ADD_ORDER_ITEM") {
    if (!Number.isSafeInteger(value.unitPriceFen) || (value.unitPriceFen as number) < 0) {
      return false;
    }
  } else if (
    value.kind !== "GIFT_ORDER_ITEM" ||
    !Number.isSafeInteger(value.listUnitPriceFen) ||
    (value.listUnitPriceFen as number) <= 0 ||
    !isBoundedString(value.reason, 2, 200) ||
    value.reason !== value.reason.trim() ||
    !isBoundedString(value.originalAuthorizerId, 1, 128) ||
    value.originalAuthorizerId !== value.originalAuthorizerId.trim() ||
    !isBoundedString(value.originalAuthorizerDisplayName, 1, 120) ||
    value.originalAuthorizerDisplayName !== value.originalAuthorizerDisplayName.trim()
  ) {
    return false;
  }
  return value.inventoryManaged
    ? Number.isSafeInteger(value.stockOnHandBefore)
    : value.stockOnHandBefore === null;
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: OrderItemAddCommand,
  state: OrderItemAddRecoveryState,
): OrderItemAddRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze({
      ...structuredClone(command),
      baselineItems: Object.freeze(
        command.baselineItems.map((item) => Object.freeze(structuredClone(item))),
      ),
    }),
    state,
  });
}

function immutableCollection(
  recoveries: readonly OrderItemAddRecovery[],
): readonly OrderItemAddRecovery[] {
  return Object.freeze([...recoveries]);
}

function storedCollection(value: unknown): readonly OrderItemAddRecovery[] | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recoveries"]) ||
    value.version !== 1 ||
    !Array.isArray(value.recoveries) ||
    value.recoveries.length === 0 ||
    value.recoveries.length > MAX_PENDING_ORDER_ITEM_ADDS
  ) {
    return null;
  }
  const recoveries: OrderItemAddRecovery[] = [];
  for (const candidate of value.recoveries) {
    if (
      !isRecord(candidate) ||
      !hasExactKeys(candidate, ["ownerIdentityKey", "command", "state"]) ||
      !isIdentityKey(candidate.ownerIdentityKey) ||
      !isOrderItemAddCommand(candidate.command) ||
      (candidate.state !== "CLAIMED" && candidate.state !== "OUTCOME_UNKNOWN")
    ) {
      return null;
    }
    recoveries.push(
      immutableRecovery(candidate.ownerIdentityKey, candidate.command, candidate.state),
    );
  }
  const owner = recoveries[0]?.ownerIdentityKey;
  const sessionId = recoveries[0]?.command.sessionId;
  if (
    !owner ||
    !sessionId ||
    recoveries.some(
      (recovery) => recovery.ownerIdentityKey !== owner || recovery.command.sessionId !== sessionId,
    ) ||
    new Set(recoveries.map((recovery) => `${recovery.command.kind}\u0000${recovery.command.skuId}`))
      .size !== recoveries.length ||
    new Set(recoveries.map((recovery) => recovery.command.idempotencyKey)).size !==
      recoveries.length
  ) {
    return null;
  }
  return immutableCollection(recoveries);
}

function collectionsEqual(
  left: readonly OrderItemAddRecovery[],
  right: readonly OrderItemAddRecovery[],
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStoredCollection(storage: Storage): readonly OrderItemAddRecovery[] | null {
  const encoded = storage.getItem(ORDER_ITEM_ADD_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedCollection(JSON.parse(encoded) as unknown);
}

function saveStoredCollection(recoveries: readonly OrderItemAddRecovery[]): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      ORDER_ITEM_ADD_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recoveries }),
    );
    const stored = readStoredCollection(access.storage);
    return stored !== null && collectionsEqual(stored, recoveries);
  } catch {
    return false;
  }
}

function verifyStoredCollection(recoveries: readonly OrderItemAddRecovery[]): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const stored = readStoredCollection(access.storage);
    return stored !== null && collectionsEqual(stored, recoveries);
  } catch {
    return false;
  }
}

function hydrateStoredCollection(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(ORDER_ITEM_ADD_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = storedCollection(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovered = immutableCollection(
      stored.map((entry) =>
        entry.state === "CLAIMED"
          ? immutableRecovery(entry.ownerIdentityKey, entry.command, "OUTCOME_UNKNOWN")
          : entry,
      ),
    );
    if (!collectionsEqual(stored, recovered) && !saveStoredCollection(recovered)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecoveries = recovered;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readOrderItemAddRecoveries(identityKey: string): OrderItemAddRecoveryView {
  hydrateStoredCollection();
  if (recoveryUnavailable || !isIdentityKey(identityKey)) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  if (pendingRecoveries.length === 0) return { status: "NONE" };
  if (pendingRecoveries.some((entry) => entry.ownerIdentityKey !== identityKey)) {
    return { status: "OTHER_OPERATOR" };
  }
  return { status: "OWNED", recoveries: pendingRecoveries };
}

export function claimOrderItemAddRecovery(
  identityKey: string,
  command: OrderItemAddCommand,
): OrderItemAddRecoveryClaim {
  const current = readOrderItemAddRecoveries(identityKey);
  if (current.status === "OTHER_OPERATOR" || current.status === "RECOVERY_STORAGE_UNAVAILABLE") {
    return current;
  }
  if (!isIdentityKey(identityKey) || !isOrderItemAddCommand(command)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  if (current.status === "OWNED") {
    if (current.recoveries[0]?.command.sessionId !== command.sessionId) {
      return { status: "OTHER_SESSION" };
    }
    const existing = current.recoveries.find(
      (entry) => entry.command.kind === command.kind && entry.command.skuId === command.skuId,
    );
    if (existing) return { status: "OWNED", recovery: existing, recoveries: current.recoveries };
    if (current.recoveries.length >= MAX_PENDING_ORDER_ITEM_ADDS) {
      recoveryUnavailable = true;
      return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
    }
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  const next = immutableCollection([...pendingRecoveries, recovery]);
  if (!saveStoredCollection(next)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  pendingRecoveries = next;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery, recoveries: next };
}

function verifyRecoveryForDispatch(recovery: OrderItemAddRecovery, identityKey: string): boolean {
  hydrateStoredCollection();
  if (
    recoveryUnavailable ||
    recovery.ownerIdentityKey !== identityKey ||
    !pendingRecoveries.includes(recovery) ||
    !verifyStoredCollection(pendingRecoveries)
  ) {
    recoveryUnavailable = true;
    return false;
  }
  return true;
}

export function beginOrderItemAddDispatch(
  recovery: OrderItemAddRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecoveries.has(recovery) || !verifyRecoveryForDispatch(recovery, identityKey)) {
    return false;
  }
  dispatchingRecoveries.add(recovery);
  return true;
}

export function finishOrderItemAddDispatch(
  recovery: OrderItemAddRecovery,
  identityKey: string,
): boolean {
  if (recovery.ownerIdentityKey !== identityKey || !dispatchingRecoveries.has(recovery))
    return false;
  dispatchingRecoveries.delete(recovery);
  return true;
}

export function markOrderItemAddOutcomeUnknown(
  recovery: OrderItemAddRecovery,
  identityKey: string,
): boolean {
  if (!verifyRecoveryForDispatch(recovery, identityKey)) return false;
  const index = pendingRecoveries.indexOf(recovery);
  if (index < 0) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  const next = immutableCollection(
    pendingRecoveries.map((entry, entryIndex) => (entryIndex === index ? unknown : entry)),
  );
  if (!saveStoredCollection(next)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecoveries = next;
  pendingOutcomeMayExist = next.length > 0;
  return true;
}

export function clearOrderItemAddRecovery(
  recovery: OrderItemAddRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    recovery.ownerIdentityKey !== identityKey ||
    !pendingRecoveries.includes(recovery)
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  const next = immutableCollection(pendingRecoveries.filter((entry) => entry !== recovery));
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      const stored = readStoredCollection(access.storage);
      if (!stored || !collectionsEqual(stored, pendingRecoveries)) {
        recoveryUnavailable = true;
        return false;
      }
      if (next.length === 0) {
        access.storage.removeItem(ORDER_ITEM_ADD_RECOVERY_STORAGE_KEY);
        if (access.storage.getItem(ORDER_ITEM_ADD_RECOVERY_STORAGE_KEY) !== null) {
          recoveryUnavailable = true;
          return false;
        }
      } else {
        access.storage.setItem(
          ORDER_ITEM_ADD_RECOVERY_STORAGE_KEY,
          JSON.stringify({ version: 1, recoveries: next }),
        );
        const saved = readStoredCollection(access.storage);
        if (!saved || !collectionsEqual(saved, next)) {
          recoveryUnavailable = true;
          return false;
        }
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecoveries = next;
  pendingOutcomeMayExist = next.length > 0;
  return true;
}

export function resetOrderItemAddRecoveryForTests(): void {
  pendingRecoveries = Object.freeze([]);
  dispatchingRecoveries.clear();
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(ORDER_ITEM_ADD_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup also resets the in-memory fail-closed state.
  }
}

export function resetOrderItemAddRecoveryMemoryForTests(): void {
  pendingRecoveries = Object.freeze([]);
  dispatchingRecoveries.clear();
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
}
